import { useDisclosure } from '@chakra-ui/react'
import cn from 'clsx'
import { FC, MouseEvent, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import DialogWindow from '@/components/DialogWindow'
import { Typography } from '@/components/ui'
import CustomInput from '@/components/ui/input/CustomInput'

import { ShotlistsService } from '@/services/shotlist.service'

import PencilGray from '@/assets/icons/pencil-gray.svg'

import { useAuth } from '@/auth/useAuth'
import { useRenameTask } from '@/features/Live/hooks/useRenameTask'
import { ITask } from '@/types/task.interface'
import { formatDate } from '@/utils/formatDate'
import { toastError } from '@/utils/toastError'

import Status from './Status'
import { DraftsContext } from './shotlistUpload/DraftsContext'

type ITab = 'files' | 'lives'

interface IProps {
	tasks: ITask[]
}

const ContentList: FC<IProps> = ({ tasks }) => {
	const [tab, setTab] = useState<ITab>('files')
	const [files, setFiles] = useState<ITask[]>([])
	const [lives, setLives] = useState<ITask[]>([])
	const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
	const [taskName, setTaskName] = useState('')

	const { renameTask } = useRenameTask()

	const navigate = useNavigate()
	const { isMain } = useAuth()
	const { removeDraft } = useContext(DraftsContext)

	const { open, onOpen, onClose } = useDisclosure()

	const tabs: { key: ITab; label: string }[] = [
		{ key: 'files', label: 'Files' },
		{ key: 'lives', label: 'Lives' }
	]

	useEffect(() => {
		if (!tasks) return

		setFiles(tasks.filter(task => task.source_type === 'shotlist-file'))
		setLives(
			tasks.filter(
				task =>
					['failed', 'finished'].includes(task.status) &&
					task.source_type === 'listener'
			)
		)
	}, [tasks])

	const openTask = async (id: string) => {
		if (tab === 'lives') {
			navigate(`/live/${id}`, {})
		} else {
			try {
				const shotlist = await ShotlistsService.getShotlist(id)

				if (shotlist && shotlist.has_video) {
					removeDraft(id)
					navigate(`/file/${id}`, {})
				} else {
					return
				}
			} catch (error) {
				console.log(error)
			}
		}
	}

	const getHref = (id: string, status: ITask['status']) => {
		if (status === 'in_progress') return undefined
		return tab === 'lives' ? `/live/${id}` : `/file/${id}`
	}

	const handleRowClick = async (
		e: MouseEvent<HTMLAnchorElement>,
		id: string,
		status: ITask['status']
	) => {
		if (status === 'in_progress') {
			e.preventDefault()
			return
		}

		if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return

		e.preventDefault()
		await openTask(id)
	}

	if (!tasks) return

	const data = tab === 'lives' ? lives : files

	const handleClose = () => {
		setSelectedTaskId(null)
		setTaskName('')
		onClose()
	}

	const handleClick = async () => {
		const trimmedTaskName = taskName.trim()

		if (!selectedTaskId) return

		if (!trimmedTaskName) {
			toastError(`The title is empty. Please enter some value.`)
			return
		}

		await renameTask({ taskId: selectedTaskId, source: trimmedTaskName })
		handleClose()
	}

	return (
		<div className='flex flex-col gap-2'>
			<Typography variant='h2' className='text-[24px] !font-[700] align-middle'>
				Archive
			</Typography>

			{/* Tabs */}
			<div className='flex items-center mb-6'>
				{tabs.map(item => (
					<button
						key={item.key}
						onClick={() => setTab(item.key)}
						className={cn(
							'px-6 py-[18px] text-[15px] font-[700] transition-colors first:px-0 leading-[16px] align-middle border-b-[4px]',
							item.key === tab
								? 'text-black border-b-black'
								: 'text-gray-400 border-b-gray-300'
						)}
					>
						{item.label} ({item.key === 'files' ? files.length : lives.length})
					</button>
				))}
			</div>

			<div>
				{/* Header */}
				<div className='flex border-b border-gray-300'>
					<div className='w-[40%] text-gray-600 text-sm py-2 min-w-[150px]'>
						Name
					</div>

					<div
						className={cn(
							'flex-1 text-gray-600 text-sm py-2 pl-4',
							tab === 'files' && 'max-[1700px]:w-[150px]'
						)}
					>
						Added
					</div>

					{tab === 'files' && (
						<>
							<div className='flex-1 text-gray-600 text-sm py-2 pl-4 max-[1250px]:w-[150px]'>
								User
							</div>

							{isMain && (
								<div className='w-[150px] max-[1920px]:w-[100px] max-[1600px]:w-[60px] text-gray-600 text-sm py-2 pl-4'>
									Place
								</div>
							)}
						</>
					)}

					<div className='w-[120px] text-gray-600 text-sm py-2 pl-4'>
						Status
					</div>
				</div>

				{/* Rows */}
				<div className='flex flex-col'>
					{data.map(task => {
						const href = getHref(task.id, task.status)

						return (
							<a
								key={task.id}
								href={href}
								onClick={e => handleRowClick(e, task.id, task.status)}
								className='flex h-14 border-b border-gray-300 transition-colors hover:bg-gray-100'
							>
								<div className='w-[40%] flex items-center gap-2 text-gray-600 min-w-[150px]'>
									<Typography variant='text' className='min-w-0 break-words'>
										{task.source}
									</Typography>

									{tab === 'lives' && (
										<div
											className='p-[2px]'
											onClick={e => {
												e.preventDefault()
												e.stopPropagation()
												setSelectedTaskId(task.id)
												setTaskName(task.source)
												onOpen()
											}}
										>
											<PencilGray />
										</div>
									)}
								</div>

								<div className='flex-1 flex items-center text-gray-600 pl-4 min-w-[100px]'>
									<Typography variant='text'>
										{formatDate(task.added_at)}
									</Typography>
								</div>

								{tab === 'files' && (
									<>
										<div className='flex items-center text-gray-600 pl-4 flex-1 min-w-0 max-[1250px]:w-[150px] max-[1250px]:flex-none'>
											<span className='min-w-0 break-all'>{task?.author}</span>
										</div>

										{isMain && (
											<div className='w-[150px] max-[1920px]:w-[100px] max-[1600px]:w-[60px] flex items-center text-gray-600 pl-4'>
												{task?.role?.slice(0, 1).toUpperCase() +
													task?.role?.slice(1).toLowerCase() || ''}
											</div>
										)}
									</>
								)}

								<div className='w-[120px] flex items-center text-gray-600 pl-4'>
									<Status status={task.status} />
								</div>
							</a>
						)
					})}
				</div>
			</div>

			<DialogWindow
				open={open}
				handleClick={handleClick}
				onClose={handleClose}
				titleText='Name live event'
				body={
					<CustomInput
						value={taskName}
						onChange={e => setTaskName(e.target.value)}
						iconName='clear'
						maxLength={100}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault()
								handleClick()
							}
						}}
					/>
				}
				noButtonText='Cancel'
				yesButtonText='Save'
				noButtonClassname='hover:bg-gray-100'
				yesButtonClassname='hover:bg-gray-600'
			/>
		</div>
	)
}

export default ContentList
